import React, { ComponentPropsWithoutRef, ComponentPropsWithRef } from 'react';

import PropTypes from 'prop-types';

import { noop } from '@adsk/alloy-react-helpers';
import { stylePropType } from '@adsk/alloy-react-theme';

import { Body, Footer, Header } from '../layout';
import Modal from '../Modal';

type Props = Omit<ComponentPropsWithRef<typeof Modal>, 'title'> & {
  className?: string;
  show?: boolean;
  footer?: React.ReactNode;
  title?: React.ReactNode;
  disableModalExit?: boolean;
  backdrop?: 'static' | true | false;
  modalProps?: ComponentPropsWithoutRef<typeof Modal>;
  modalHeaderProps?: ComponentPropsWithoutRef<typeof Header>;
  modalBodyProps?: ComponentPropsWithoutRef<typeof Body>;
  modalFooterProps?: ComponentPropsWithoutRef<typeof Footer>;
  onClose: () => void;
};

/**
 * A Simple Modal component.
 */
const SimpleModal = ({
  className,
  style,
  children,
  modalProps,
  modalHeaderProps,
  modalBodyProps,
  modalFooterProps,
  show,
  title,
  disableModalExit,
  footer,
  backdrop,
  onClose,
  ...props
}: Props) => {
  disableModalExit = disableModalExit || backdrop === 'static';

  return (
    <Modal
      className={className}
      style={style}
      open={show}
      hideOverlay={backdrop === false}
      onClose={onClose}
      {...modalProps}
      {...props}
    >
      {title && (
        <Modal.Header {...modalHeaderProps}>
          {title}
          {!disableModalExit && <Modal.Close onClick={onClose} />}
        </Modal.Header>
      )}
      {children && <Modal.Body {...modalBodyProps}>{children}</Modal.Body>}
      {footer && (
        <Modal.Footer disableBorder {...modalFooterProps}>
          {footer}
        </Modal.Footer>
      )}
    </Modal>
  );
};

SimpleModal.defaultProps = {
  show: true,
  onClose: noop,
};

SimpleModal.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,

  /**
   * Whether to show the modal or not
   */
  show: PropTypes.bool,

  /**
   * Modal content
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Optional footer
   */
  footer: PropTypes.node,
  /**
   * Text for the modal title
   */
  title: PropTypes.node,
  /**
   * Determines if the modal cannot be exited (exit button will be hidden and clickoutside will be disabled)
   */
  disableModalExit: PropTypes.bool,
  /**
   * Determines the backdrop of modal.
   */
  backdrop: PropTypes.oneOf(['static', true, false]),
  /**
   * Allows to pass props directly to the Modal component
   */
  modalProps: PropTypes.object,
  /**
   * Allows to pass props directly to the Modal.Header component
   */
  modalHeaderProps: PropTypes.object,
  /**
   * Allows to pass props directly to the Modal.Body component
   */
  modalBodyProps: PropTypes.object,
  /**
   * Allows to pass props directly to the Modal.Footer component
   */
  modalFooterProps: PropTypes.object,
  /**
   * A callback function for when trying to close the modal
   */
  onClose: PropTypes.func,
};

export default SimpleModal;
